import React from "react";
import {
  SafeAreaView,
  Text,
  TouchableOpacity,
  View,
  Image,
  PanResponder,
  Platform,
  Animated,
  Dimensions,
} from "react-native";

import { Audio, InterruptionModeAndroid, InterruptionModeIOS } from "expo-av";
import { activateKeepAwakeAsync, deactivateKeepAwake } from "expo-keep-awake";
import { FontAwesome6 } from "@expo/vector-icons";
import { Svg, Path } from "react-native-svg";

import Slider from "@react-native-community/slider";

const timer = require("react-native-timer");

import Blink from "./Blink";
import { applyPreferences, storeData, DEFAULT_SETTINGS } from "./storage";
import soundsList from "./Sound_List.js";
import styles from "./Screen_Simple.styles.js";
import { useTheme } from "./ThemeContext";

const { width: screenWidth, height: screenHeight } = Dimensions.get("window");

const iconSize =
  Platform.OS === "ios" ? 40 : Platform.OS === "android" ? 44 : 50;
const bpmPickerHeight = Platform.OS === "android" ? 250 : 230;
const bpmTrackHeight = Platform.OS === "android" ? 230 : 210;

// Responsive scaling factors
const isSmallScreen = screenWidth < 375; // iPhone SE, iPhone 13 mini
const scaleFactor = isSmallScreen ? 0.85 : 1.0;

export default function HomeWrapper(props) {
  const { themeValues } = useTheme();
  return (
    <Home themeValues={themeValues} red670nmEnabled={props.red670nmEnabled} />
  );
}

class Home extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isPlaying: false,
      isPaused: false,
      playbackInstance: null,
      currentIndex: 0,
      volume: 1.0,
      isBeating: false,
      interval: 1000,
      beatCount: 0,
      beatsPerMinute: 60,
      toBeat: 1200,
      minutesToPlay: 20,
      selectedHours: 0,
      selectedMinutes: 20,
      soundBellEnabled: DEFAULT_SETTINGS.soundBellEnabled,
      bellSound: null,
      wakeLock: null,
      isIconColored: false,
      iconColorEndTime: null,
      widerBeatRange: DEFAULT_SETTINGS.widerBeatRange,
    };

    this.animatedValue = new Animated.Value(0);

    this.sliderTrackRef = null;
    this.sliderTrackX = 0;
    this.sliderTrackWidth = 180;
    this.sliderTrackMeasured = false;

    this.sliderContainerRef = null;
    this.sliderContainerX = 0;
    this.sliderContainerMeasured = false;

    this.sliderPanResponder = PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: (e, gestureState) => this.handleSliderStart(e, gestureState),
      onPanResponderMove: (e, gestureState) => this.handleSliderMove(e, gestureState),
      onPanResponderRelease: () => this.handleSliderRelease(),
    });
  }

  /* ***************************************************************** */

  async componentDidMount() {
    try {
      await applyPreferences(this.state);

      // Check if BPM is outside current range and adjust if needed
      const maxBPM = this.state.widerBeatRange ? 120 : 75;
      if (this.state.beatsPerMinute > maxBPM) {
        const adjustedBPM = maxBPM;
        this.setState({
          beatsPerMinute: adjustedBPM,
          interval: (60 / adjustedBPM) * 1000,
          toBeat: adjustedBPM * this.state.minutesToPlay,
          beatCount: 0
        });
        // Save the adjusted BPM
        await storeData({ beatsPerMinute: adjustedBPM });
      }

      // Load bell sound
      const { sound } = await Audio.Sound.createAsync(
        require("../assets/bell.m4a"),
      );
      this.setState({ bellSound: sound });

      // Add wake lock and visibility handling for mobile browsers
      if (Platform.OS === 'web') {
        this.setupWakeLock();
        this.setupVisibilityHandler();
        this.setupUserInteraction();
      }

      // Force a re-render to ensure state is updated before loading audio
      this.setState({}, () => {
        this.setupAudioAndLoad();
      });
    } catch (e) {
      console.warn("Preferences error:", e);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Handle keep-awake for native iOS/Android when beating state changes
    if (Platform.OS !== 'web') {
      if (this.state.isBeating && !prevState.isBeating) {
        // Started beating - activate keep-awake
        activateKeepAwakeAsync();
      } else if (!this.state.isBeating && prevState.isBeating) {
        // Stopped beating - deactivate keep-awake
        deactivateKeepAwake();
      }
    }
  }

  setupWakeLock = () => {
    if ('wakeLock' in navigator) {
      navigator.wakeLock.request('screen')
        .then((wakeLock) => {
          this.setState({ wakeLock });
          console.log('Wake lock activated');
        })
        .catch((err) => {
          console.warn('Wake lock failed:', err);
        });
    }
  };

  setupVisibilityHandler = () => {
    let wasVisible = true;

    document.addEventListener('visibilitychange', () => {
      const isVisible = document.visibilityState === 'visible';

      // Only resume if we're returning to visible (was hidden, now visible)
      if (isVisible && !wasVisible && this.state.isBeating && !this.state.isPaused) {
        console.log('Page became visible, resuming audio');
        this.keepBeating();
      }

      // If page becomes hidden, pause the beating to prevent background audio
      if (!isVisible && this.state.isBeating && !this.state.isPaused) {
        console.log('Page hidden, pausing audio');
        this.setState({ isPaused: true });
      }

      wasVisible = isVisible;
    });

    // Prevent mobile browser from sleeping
    if ('screen' in navigator && 'orientation' in screen) {
      screen.orientation.addEventListener('change', () => {
        // Keep app active on orientation change
        if (this.state.isBeating && !this.state.isPaused) {
          setTimeout(() => this.keepBeating(), 100);
        }
      });
    }
  };

  setupUserInteraction = () => {
    // Create audio context on first user interaction for mobile browsers
    const enableAudio = () => {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        console.log('Audio context created for mobile');
      }
    };

    // Add event listeners for user interaction
    document.addEventListener('touchstart', enableAudio, { once: true });
    document.addEventListener('click', enableAudio, { once: true });
    document.addEventListener('keydown', enableAudio, { once: true });
  };

  componentWillUnmount() {
    // Clear icon color timeout if it exists
    if (this.state.iconColorEndTime) {
      this.setState({ iconColorEndTime: null });
    }

    // Deactivate keep-awake for native platforms
    if (Platform.OS !== 'web') {
      deactivateKeepAwake();
    }

    // Release wake lock when component unmounts
    if (this.state.wakeLock) {
      this.state.wakeLock.release();
      console.log('Wake lock released');
    }

    // Clean up event listeners
    if (Platform.OS === 'web') {
      document.removeEventListener('visibilitychange', this.setupVisibilityHandler);
      if ('screen' in navigator && 'orientation' in screen) {
        screen.orientation.removeEventListener('change', this.setupVisibilityHandler);
      }
      // Remove user interaction listeners
      document.removeEventListener('touchstart', this.setupUserInteraction);
      document.removeEventListener('click', this.setupUserInteraction);
      document.removeEventListener('keydown', this.setupUserInteraction);
    }
  }

  async setupAudioAndLoad() {
    try {
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
        interruptionModeIOS: InterruptionModeIOS.DuckOthers,
        playsInSilentModeIOS: true,
        interruptionModeAndroid: InterruptionModeAndroid.DuckOthers,
        shouldDuckAndroid: true,
        staysActiveInBackground: true,
        playThroughEarpieceAndroid: true,
      });

      this.loadAudio();
    } catch (e) {
      console.warn("Audio setup error:", e);
    }
  }

  async loadAudio() {
    const { currentIndex, isPlaying, volume } = this.state;

    try {
      // Use stable expo-av 13.10.6 API - no LegacyEventEmitter issues
      const playbackInstance = new Audio.Sound();

      // Set up status update first
      playbackInstance.setOnPlaybackStatusUpdate(this.onPlaybackStatusUpdate);

      // Load the audio file
      await playbackInstance.loadAsync(soundsList[currentIndex].uri);

      // Set initial volume
      await playbackInstance.setVolumeAsync(volume);

      this.setState({
        playbackInstance,
        isPlaying: false,
      });
    } catch (e) {
      console.warn("Audio loading error:", e);
      this.setState({ playbackInstance: null });
    }
  }

  onPlaybackStatusUpdate = (status) => {
    this.setState({
      isPlaying: status.isPlaying,
    });

    // Update icon color based on playing state and minimum end time
    this.updateIconColor(status.isPlaying);
  };

  async playBeatSound() {
    const { playbackInstance, currentIndex, beatCount } = this.state;

    if (!playbackInstance) {
      console.error("No playback instance available");
      return;
    }

    try {
      // On web, fade out and pause any playing sound first to avoid pop on Chromium browsers
      if (Platform.OS === 'web') {
        try {
          // Gradual fade out over 40ms in 4 steps
          const fadeSteps = [0.3, 0.1, 0.03, 0];
          for (const vol of fadeSteps) {
            await playbackInstance.setVolumeAsync(vol);
            await new Promise(resolve => setTimeout(resolve, 10));
          }
          await playbackInstance.pauseAsync();
          await playbackInstance.setPositionAsync(0);
          // Reset volume before replay
          await playbackInstance.setVolumeAsync(this.state.volume);
        } catch (e) {
          // Ignore errors
        }
      }

      // Trigger icon color change immediately when sound starts
      this.triggerIconColor();

      // Handle alternating sounds if alternateSound is specified
      const currentSound = soundsList[currentIndex];
      if (currentSound.alternateSound) {
        const isEvenBeat = beatCount % 2 === 0;
        const tickSound = isEvenBeat
          ? currentSound.uri
          : currentSound.alternateSound;

        // Create a temporary sound instance for the alternating sound
        const tempSound = new Audio.Sound();
        await tempSound.loadAsync(tickSound);
        await tempSound.replayAsync();

        // Clean up the temporary sound after it plays
        setTimeout(() => {
          tempSound.unloadAsync();
        }, 500);

        // For alternating sounds, manually turn off icon color after a short delay
        // since the temporary sound doesn't trigger onPlaybackStatusUpdate
        setTimeout(() => {
          this.updateIconColor(false);
        }, 200);
      } else {
        // Normal playback for all other sounds
        await playbackInstance.replayAsync();

        // Stop sound after 85% of beat interval to minimize off-time between beats
        // This prevents the quick blink at the end of each beat
        const maxSoundDuration = this.state.interval * 0.85;
        setTimeout(() => {
          const isWeb = Platform.OS === 'web';
          if (isWeb) {
            // On web, don't pause sound (causes pop) - just turn off icon color
            this.updateIconColor(false);
          } else if (this.state.playbackInstance) {
            // On native, gradual fade out over 30ms in 3 steps
            const fadeSteps = [0.3, 0.1, 0];
            const stepDuration = 10;
            fadeSteps.forEach((vol, index) => {
              setTimeout(async () => {
                try {
                  await this.state.playbackInstance.setVolumeAsync(vol);
                  if (vol === 0) {
                    await this.state.playbackInstance.pauseAsync();
                    // Reset volume for next beat
                    await this.state.playbackInstance.setVolumeAsync(this.state.volume);
                  }
                } catch (e) {
                  // Ignore errors if sound already stopped
                }
              }, index * stepDuration);
            });
          }
        }, maxSoundDuration);
      }
    } catch (e) {
      console.warn("Beat sound error:", e);
    }
  }

  updateIconColor = (isPlaying) => {
    if (isPlaying) {
      // Sound is playing, turn on color and clear any end time
      this.setState({ isIconColored: true, iconColorEndTime: null });
    } else {
      // Sound stopped, only turn off color if minimum time has passed
      const now = Date.now();
      if (!this.state.iconColorEndTime || now >= this.state.iconColorEndTime) {
        this.setState({ isIconColored: false, iconColorEndTime: null });
      }
    }
  }

  triggerIconColor = () => {
    // Set icon to colored immediately
    this.setState({ isIconColored: true });

    // Set minimum display time of 100ms from now (reduced from 200ms to prevent double blink)
    const endTime = Date.now() + 100;
    this.setState({ iconColorEndTime: endTime });
  }

  handlePlaySound = async () => {
    const { isPlaying, playbackInstance } = this.state;

    if (!playbackInstance) {
      console.error("No playback instance available");
      return;
    }

    try {
      if (isPlaying) {
        await playbackInstance.stopAsync();
      } else {
        // Trigger icon color change immediately when sound starts
        this.triggerIconColor();
        await playbackInstance.playAsync();
      }
    } catch (e) {
      console.warn("Playback error:", e);
    }
  }

  updateIconColor = (isPlaying) => {
    if (isPlaying) {
      // Sound is playing, turn on color and clear any end time
      this.setState({ isIconColored: true, iconColorEndTime: null });
    } else {
      // Sound stopped, only turn off color if minimum time has passed
      const now = Date.now();
      if (!this.state.iconColorEndTime || now >= this.state.iconColorEndTime) {
        this.setState({ isIconColored: false, iconColorEndTime: null });
      }
    }
  }

  triggerIconColor = () => {
    // Set icon to colored immediately
    this.setState({ isIconColored: true });

    // Set minimum display time of 100ms from now (reduced from 200ms to prevent double blink)
    const endTime = Date.now() + 100;
    this.setState({ iconColorEndTime: endTime });
  }

  handlePreviousSound = async () => {
    let { playbackInstance, currentIndex } = this.state;

    if (playbackInstance) {
      await playbackInstance.unloadAsync();
      currentIndex > 0
        ? (currentIndex -= 1)
        : (currentIndex = soundsList.length - 1);

      this.setState({ currentIndex }, () => {
        storeData({ currentIndex });
        this.loadAudio();
      });
    } else {
      console.error("No playback instance available");
    }
  };

  handleNextSound = async () => {
    let { playbackInstance, currentIndex } = this.state;

    if (playbackInstance) {
      await playbackInstance.unloadAsync();
      currentIndex < soundsList.length - 1
        ? (currentIndex += 1)
        : (currentIndex = 0);

      this.setState({ currentIndex }, () => {
        storeData({ currentIndex });
        this.loadAudio();
      });
    } else {
      console.error("No playback instance available");
    }
  };

  renderSoundInfo() {
    const { playbackInstance, currentIndex } = this.state;
    return playbackInstance ? (
      <View style={styles.soundInfo}>
        <Text style={[styles.soundInfoText, styles.largeText]}>
          {soundsList[currentIndex].title}
        </Text>
      </View>
    ) : null;
  }

  /* ***************************************************************** */

  keepBeating() {
    // Don't start beating if page is hidden
    if (Platform.OS === 'web' && document.visibilityState === 'hidden') {
      console.log('Page hidden, not starting audio');
      return;
    }

    // Ensure wake lock is active on mobile
    if (Platform.OS === 'web' && !this.state.wakeLock) {
      this.setupWakeLock();
    }

    this.playBeatSound();
    this.setState({ isBeating: true }, () =>
      timer.setTimeout(
        this,
        "hideMsg",
        () => {
          const newBeatCount = this.state.beatCount + 1;
          this.setState({
            beatCount: newBeatCount,
          });

          // Check if session is complete
          if (newBeatCount >= this.state.toBeat) {
            // Only play bell when session actually ends (1 beat left)
            if (newBeatCount === this.state.toBeat) {
              this.playBellSound();
            }
            this.setState({
              isBeating: false,
              isPaused: false,
            });
          } else if (!this.state.isPaused) {
            this.keepBeating();
          }
        },
        this.state.interval,
      ),
    );
  }

  async playBellSound() {
    const { bellSound, soundBellEnabled } = this.state;
    if (bellSound && soundBellEnabled) {
      try {
        await bellSound.replayAsync();
      } catch (e) {
        console.warn("Bell sound error:", e);
      }
    }
  }

  onChangedRate(rate) {
    const state = {
      beatsPerMinute: rate,
      interval: (60 / rate) * 1000,
      toBeat: rate * this.state.minutesToPlay,
      beatCount: 0,
    };
    // The control display won't update with the selected value
    //   if setState is called from storeData
    this.setState(state);
    storeData(state);
  }
  onChangedTime(selected) {
    let hours = selected.hours;
    let min = parseInt(selected.minutes);

    if (hours > 0) {
      min += hours * 60;
    }
    const state = {
      selectedHours: parseInt(selected.hours),
      selectedMinutes: parseInt(selected.minutes),
      minutesToPlay: min,
      toBeat: this.state.beatsPerMinute * min,
      beatCount: 0,
    };

    // The control display won't update with the selected value
    //   if setState is called from storeData
    this.setState(state);
    storeData(state);
  }

  // Draggable number component
  createDraggableNumber = (value, type, range) => {
    const panResponder = PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: () => {
        this.animatedValue.setOffset(this.animatedValue._value);
        this.animatedValue.setValue(0);
      },
      onPanResponderMove: (evt, gestureState) => {
        this.animatedValue.setValue(gestureState.dy);

        // Calculate new value based on drag distance
        const dragDistance = gestureState.dy;
        const sensitivity = 20; // pixels per increment
        const increments = Math.round(-dragDistance / sensitivity);

        if (increments !== 0) {
          let newValue = value + increments;

          // Apply range constraints
          if (type === 'hours') {
            newValue = Math.max(0, Math.min(23, newValue));
          } else if (type === 'minutes') {
            newValue = Math.max(0, Math.min(59, newValue));
          }

          if (newValue !== value) {
            this.onChangedTime({
              hours: type === 'hours' ? newValue : this.state.selectedHours,
              minutes: type === 'minutes' ? newValue : this.state.selectedMinutes
            });
          }
        }
      },
      onPanResponderRelease: () => {
        this.animatedValue.flattenOffset();
        Animated.spring(this.animatedValue, {
          toValue: 0,
          useNativeDriver: true,
        }).start();
      },
    });

    return panResponder;
  };

  showTimePicker(type, isLongPress) {
    const { selectedHours, selectedMinutes } = this.state;

    if (type === "hours") {
      // Cycle through hours: 0, 1, 2, ..., 23
      const newHours = isLongPress
        ? (selectedHours + 1) % 24
        : Math.max(0, selectedHours - 1);
      this.onChangedTime({ hours: newHours, minutes: selectedMinutes });
    } else if (type === "minutes") {
      // Use 1-minute increments for first 10-15 minutes, then 5-minute increments
      const newMinutes = isLongPress
        ? selectedMinutes < 10
          ? (selectedMinutes + 1) % 11
          : selectedMinutes < 15
            ? (selectedMinutes + 1) % 16
            : (selectedMinutes + 5) % 60
        : Math.max(0, selectedMinutes - 1);
      this.onChangedTime({ hours: selectedHours, minutes: newMinutes });
    }
  }

  /* ***************************************************************** */

  getRemainingTime(time) {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);

    if (hours > 0) {
      return hours + ":" + `${minutes}`.padStart(2, "0") + ":" + `${seconds}`.padStart(2, "0");
    } else {
      return minutes + ":" + `${seconds}`.padStart(2, "0");
    }
  }

  /* Custom Vertical Slider Handlers */
  bpmFromSliderX = (sliderX) => {
    const sliderWidth = this.sliderTrackWidth || 180;
    const minValue = 10;
    const maxValue = this.state.widerBeatRange ? 120 : 75;
    const percentage = Math.max(0, Math.min(1, sliderX / sliderWidth));
    return Math.round(minValue + (maxValue - minValue) * percentage);
  };

  handleSliderTrackLayout = () => {
    if (!this.sliderTrackRef || !this.sliderTrackRef.measureInWindow) return;
    requestAnimationFrame(() => {
      if (!this.sliderTrackRef || !this.sliderTrackRef.measureInWindow) return;
      this.sliderTrackRef.measureInWindow((x, y, width) => {
        if (typeof x === 'number') this.sliderTrackX = x;
        if (typeof width === 'number' && width > 0) this.sliderTrackWidth = width;
        this.sliderTrackMeasured = true;
      });
    });
  };

  handleSliderContainerLayout = () => {
    if (!this.sliderContainerRef || !this.sliderContainerRef.measureInWindow) return;
    requestAnimationFrame(() => {
      if (!this.sliderContainerRef || !this.sliderContainerRef.measureInWindow) return;
      this.sliderContainerRef.measureInWindow((x, y, width, height) => {
        if (typeof x === 'number') this.sliderContainerX = x;
        this.sliderContainerMeasured = true;
      });
    });
  };

  getSliderXFromEvent = (e, gestureState) => {
    const touchEvent = e.nativeEvent;

    if (Platform.OS === 'web') {
      if (touchEvent.locationX !== undefined) {
        return touchEvent.locationX;
      }
      if (touchEvent.clientX !== undefined) {
        return Math.max(0, Math.min(180, touchEvent.clientX % 180));
      }
      return 90;
    }

    const absoluteX =
      typeof gestureState?.moveX === 'number' && gestureState.moveX > 0
        ? gestureState.moveX
        : typeof gestureState?.x0 === 'number'
          ? gestureState.x0
          : touchEvent.pageX;

    // Native primary path: compute relative X from measured container.
    if (typeof absoluteX === 'number' && this.sliderContainerMeasured) {
      const relToContainer = absoluteX - (this.sliderContainerX || 0);
      // Track is centered within 200px container with 10px inset.
      return Math.max(0, Math.min(180, relToContainer - 10));
    }

    if (
      typeof absoluteX === 'number' &&
      this.sliderTrackMeasured &&
      (this.sliderTrackWidth || 0) > 0
    ) {
      const width = this.sliderTrackWidth || 180;
      const rel = absoluteX - (this.sliderTrackX || 0);
      return Math.max(0, Math.min(width, rel));
    }

    if (touchEvent.locationX !== undefined) {
      // PanResponder is on the 200px container; track is centered with a 10px inset.
      return Math.max(0, Math.min(180, touchEvent.locationX - 10));
    }

    return 0;
  };

  handleSliderStart = (e, gestureState) => {
    this.isSliding = true;

    const sliderX = this.getSliderXFromEvent(e, gestureState);

    const minValue = 10;
    const denom = this.state.widerBeatRange ? 110 : 65;
    const trackWidth = this.sliderTrackWidth || 180;
    const thumbLeft = ((this.state.beatsPerMinute - minValue) / denom) * trackWidth;
    const thumbRight = thumbLeft + 20;

    // Only treat it as a thumb drag if the touch starts on the thumb itself.
    // If you tap beside it, we want to snap immediately.
    this.isDraggingThumb = sliderX >= thumbLeft && sliderX <= thumbRight;

    if (this.isDraggingThumb) {
      this.dragStartThumbLeft = thumbLeft;
      this.dragStartGestureX = gestureState?.dx || 0;
      return;
    }

    const value = this.bpmFromSliderX(sliderX);
    if (__DEV__ && Platform.OS !== 'web' && value === 10) {
      console.log('[BPM slider] snap->min debug', {
        sliderX,
        sliderContainerMeasured: this.sliderContainerMeasured,
        sliderContainerX: this.sliderContainerX,
        sliderTrackMeasured: this.sliderTrackMeasured,
        sliderTrackX: this.sliderTrackX,
        sliderTrackWidth: this.sliderTrackWidth,
        x0: gestureState?.x0,
        moveX: gestureState?.moveX,
        locationX: e?.nativeEvent?.locationX,
        pageX: e?.nativeEvent?.pageX,
      });
    }
    this.onChangedRate(value);
  };

  handleSliderMove = (e, gestureState) => {
    if (!this.isSliding) return;

    if (this.isDraggingThumb) {
      const dx = (gestureState?.dx || 0) - (this.dragStartGestureX || 0);
      const newThumbLeft = Math.max(0, Math.min(180, (this.dragStartThumbLeft || 0) + dx));
      const value = this.bpmFromSliderX(newThumbLeft);
      this.onChangedRate(value);
      return;
    }

    // While holding the track (not the thumb), follow the finger.
    const sliderX = this.getSliderXFromEvent(e, gestureState);
    const value = this.bpmFromSliderX(sliderX);
    this.onChangedRate(value);
  };

  handleSliderRelease = () => {
    this.isSliding = false;
    this.isDraggingThumb = false;
  };

  handleVerticalSliderPress = (e) => {
    this.updateSliderValue(e);
  };

  updateSliderValue = (e) => {
    const sliderX = this.getSliderXFromEvent(e);
    const value = this.bpmFromSliderX(sliderX);
    this.onChangedRate(value);
  };

  renderIcon = (sound, isPlaying) => {
    // Use iconType field to determine how to render the icon
    if (sound.iconType === "svg") {
      // Extract the path data from the SVG string
      const svgContent = isPlaying ? sound.iconPlaying : sound.iconNot;
      const pathMatch = svgContent.match(/d="([^"]+)"/);
      const viewBoxMatch = svgContent.match(/viewBox="([^"]+)"/);

      if (pathMatch && viewBoxMatch) {
        const mainIconSize =
          Platform.OS === "ios" ? 260 : Platform.OS === "android" ? 300 : 350;
        return (
          <Svg
            width={mainIconSize}
            height={mainIconSize}
            viewBox={viewBoxMatch[1]}
          >
            <Path
              d={pathMatch[1]}
              fill={this.state.isIconColored ? sound.iconPlayingColor : "#222"}
            />
          </Svg>
        );
      }
    } else if (sound.iconType === "image") {
      // It's an image file
      if (sound.imageNot) {
        const imageSource = isPlaying ? sound.imagePlay : sound.imageNot;
        const mainIconSize =
          Platform.OS === "ios" ? 260 : Platform.OS === "android" ? 300 : 350;
        return (
          <Image
            source={
              typeof imageSource === "string"
                ? { uri: imageSource }
                : require("../assets/monitor.png")
            }
            style={{ width: mainIconSize, height: mainIconSize }}
            resizeMode="contain"
          />
        );
      }
    }

    // Default to FontAwesome (either iconType is 'fontawesome' or not specified)
    const iconName = isPlaying ? sound.iconPlaying : sound.iconNot;
    const iconColor = this.state.isIconColored ? sound.iconPlayingColor : "#222";
    const mainIconSize =
      Platform.OS === "ios" ? 260 : Platform.OS === "android" ? 300 : 350;

    // Add static rotation for Deep Drum only
    const isDeepDrum = sound.title.toLowerCase().includes('deep drum');
    const iconStyle = isDeepDrum ? {
      transform: [{ rotate: '90deg' }, { scaleX: -1 }], // Rotate and flip vertically
    } : {};

    const iconElement = (
      <FontAwesome6
        style={iconStyle}
        color={iconColor}
        name={iconName}
        size={mainIconSize * scaleFactor}
      />
    );

    // Wrap all icons in a container on iOS to prevent cutoff
    // Also wrap Deep Drum icon on Android to center it properly after transform
    if (Platform.OS === 'ios') {
      // Icons with taller aspect ratios need more height
      const needsExtraHeight = iconName === 'clock' || iconName === 'wind';
      const containerHeight = needsExtraHeight ? mainIconSize * 1.3 : mainIconSize * 1.1;

      return (
        <View style={{
        width: mainIconSize * 1.2 * scaleFactor,
        height: containerHeight * scaleFactor,
        justifyContent: 'center',
        alignItems: 'center'
      }}>
          {iconElement}
        </View>
      );
    } else if (Platform.OS === 'android') {
      // Android icons need container to prevent cutoff by progress bar
      const containerHeight = isDeepDrum ? mainIconSize * 1.2 : mainIconSize * 1.15;
      return (
        <View style={{
          width: mainIconSize * 1.2 * scaleFactor,
          height: containerHeight * scaleFactor,
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {iconElement}
        </View>
      );
    }

    return iconElement;
  };

  /* ***************************************************************** */

  render() {
    const { selectedHours, selectedMinutes } = this.state;
    let { currentIndex } = this.state;

    return (
      <SafeAreaView
        style={[
          styles.container,
          {
            backgroundColor: this.props.themeValues.background,
            transform: [{ scale: scaleFactor }]
          }
        ]}
      >
        <View
          style={[
            styles.soundIconBox,
            { backgroundColor: this.props.themeValues.background },
          ]}
        >
          {this.renderIcon(soundsList[currentIndex], this.state.isPlaying)}
        </View>
        <View
          style={[
            styles.contolBox,
            {
              backgroundColor: this.props.red670nmEnabled
                ? "#CC0000"
                : this.props.themeValues.background,
              position: "relative",
            },
          ]}
        >
          <View style={{ marginHorizontal: 0, paddingHorizontal: 20 }}>
            <View
              style={{
                height: 16,
                backgroundColor: this.props.themeValues.border,
                borderRadius: 8,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <View
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: `${this.state.isBeating || this.state.isPaused ? (this.state.beatCount / this.state.toBeat) * 100 : 0}%`,
                  backgroundColor: this.props.themeValues.primary,
                  borderRadius: 8,
                }}
              />
            </View>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                marginTop: 8,
                paddingHorizontal: 0,
              }}
            >
              <Text style={{ fontSize: 14, color: this.props.themeValues.text }}>
                {this.state.isBeating || this.state.isPaused
                  ? this.getRemainingTime(
                      (this.state.interval * this.state.beatCount) / 1000,
                    )
                  : "0:00"}
              </Text>
              <Text style={{ fontSize: 14, color: this.props.themeValues.text }}>
                {this.state.isBeating || this.state.isPaused
                  ? this.getRemainingTime(
                      (this.state.interval * this.state.toBeat) / 1000,
                    )
                  : `${this.state.selectedHours}:${this.state.selectedMinutes < 10 ? "0" + this.state.selectedMinutes : this.state.selectedMinutes}:00`}
              </Text>
            </View>
          </View>
          <View style={styles.controls}>
            <TouchableOpacity
              style={styles.control}
              onPress={this.handlePreviousSound}
            >
              <FontAwesome6 name={"arrow-left"} size={iconSize} color="#444" />
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.control}
              onPress={this.handlePlaySound}
            >
              {this.renderSoundInfo()}
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.control}
              onPress={this.handleNextSound}
            >
              <FontAwesome6 name={"arrow-right"} size={iconSize} color="#444" />
            </TouchableOpacity>
          </View>

          {/* <View style={[styles.centered]}>
            {this.state.isBeating || this.state.isPaused ? (
              <View>
                <Text style={styles.largeText}>
                  Time remaining:{" "}
                  {this.getRemainingTime(
                    (this.state.interval *
                      (this.state.toBeat - this.state.beatCount)) /
                      1000
                  )}
                </Text>
                <Text>
                  Beats remaining: {this.state.toBeat - this.state.beatCount}
                </Text>
              </View>
            ) : null}
          </View> */}

          <View style={[styles.centered, styles.control]}>
            {this.state.isBeating || this.state.isPaused ? (
              <View style={styles.controls}>
                <TouchableOpacity
                  style={styles.control}
                  onPress={() =>
                    requestAnimationFrame(() => {
                      this.setState({ isPaused: !this.state.isPaused });
                      this.keepBeating();
                    })
                  }
                >
                  {this.state.isPaused ? (
                    <Blink duration={this.state.interval}>
                      <FontAwesome6
                        name={"pause"}
                        size={iconSize * 1.5}
                        color="#444"
                      />
                    </Blink>
                  ) : (
                    <FontAwesome6
                      name={"pause"}
                      size={iconSize * 1.5}
                      color="#444"
                    />
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.control}
                  onPress={() =>
                    requestAnimationFrame(() => {
                      this.setState({
                        beatCount: this.state.toBeat,
                        isBeating: false,
                        isPaused: false,
                      });
                    })
                  }
                >
                  <FontAwesome6
                    name={"stop"}
                    size={iconSize * 1.5}
                    color="#444"
                  />
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity
                style={styles.control}
                onPress={() =>
                  requestAnimationFrame(() => {
                    this.setState({ beatCount: 0 });
                    this.keepBeating();
                  })
                }
              >
                <FontAwesome6
                  name={"play"}
                  size={iconSize * 1.5}
                  color="#444"
                />
              </TouchableOpacity>
            )}
          </View>

          <View
            style={[
              styles.centered,
              {
                flexDirection: "column",
                justifyContent: "space-around",
                alignItems: "center",
                marginTop: 30,
              },
            ]}
          >
            <View style={[styles.centered, { marginBottom: 20 }]}>
              {Platform.OS === 'web' ? (
                // Web version - traditional inputs
                <View style={[styles.timePicker, isSmallScreen && styles.timePickerSmall, { flexDirection: "row", backgroundColor: this.props.themeValues.secondary }]}>
                  <View style={[styles.timeColumn, isSmallScreen && styles.timeColumnSmall]}>
                    <Text style={[styles.timeLabel, isSmallScreen && styles.timeLabelSmall, { color: this.props.themeValues.muted }]}>Hours</Text>
                    <View style={styles.webTimeInput}>
                      <TouchableOpacity
                        style={styles.webTimeButton}
                        onPress={() => this.showTimePicker("hours", true)}
                      >
                        <Text style={[styles.timeText, isSmallScreen && styles.timeTextSmall, { color: this.props.themeValues.text }]}>{selectedHours}</Text>
                      </TouchableOpacity>
                      <View style={[styles.webTimeArrows, { marginLeft: selectedHours < 10 ? -26 : -12 }]}>
                        <TouchableOpacity
                          style={styles.webArrow}
                          onPress={() => this.showTimePicker("hours", true)}
                        >
                          <Text style={[styles.webArrowText, { color: this.props.themeValues.text }]}>▲</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.webArrow}
                          onPress={() => this.showTimePicker("hours")}
                        >
                          <Text style={[styles.webArrowText, { color: this.props.themeValues.text }]}>▼</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>

                  <View style={[styles.timeColumn, isSmallScreen && styles.timeColumnSmall]}>
                    <Text style={[styles.timeLabel, isSmallScreen && styles.timeLabelSmall, { color: this.props.themeValues.muted }]}>Minutes</Text>
                    <View style={styles.webTimeInput}>
                      <TouchableOpacity
                        style={styles.webTimeButton}
                        onPress={() => this.showTimePicker("minutes", true)}
                      >
                        <Text style={[styles.timeText, isSmallScreen && styles.timeTextSmall, { color: this.props.themeValues.text }]}>
                          {selectedMinutes < 10 ? "0" + selectedMinutes : selectedMinutes}
                        </Text>
                      </TouchableOpacity>
                      <View style={styles.webTimeArrows}>
                        <TouchableOpacity
                          style={styles.webArrow}
                          onPress={() => this.showTimePicker("minutes", true)}
                        >
                          <Text style={[styles.webArrowText, { color: this.props.themeValues.text }]}>▲</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.webArrow}
                          onPress={() => this.showTimePicker("minutes")}
                        >
                          <Text style={[styles.webArrowText, { color: this.props.themeValues.text }]}>▼</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                </View>
              ) : (
                // Mobile version - draggable numbers
                <View style={[styles.timePicker, isSmallScreen && styles.timePickerSmall, { flexDirection: "row", backgroundColor: this.props.themeValues.secondary }]}>
                  <View style={[styles.timeColumn, isSmallScreen && styles.timeColumnSmall]}>
                    <Text style={[styles.timeLabel, isSmallScreen && styles.timeLabelSmall, { color: this.props.themeValues.muted }]}>Hours</Text>
                    <Animated.View
                      {...this.createDraggableNumber(selectedHours, 'hours', { min: 0, max: 23 }).panHandlers}
                      style={[
                        styles.draggableNumber,
                        isSmallScreen && styles.draggableNumberSmall,
                        {
                          transform: [{
                            translateY: this.animatedValue.interpolate({
                              inputRange: [-100, 100],
                              outputRange: [-10, 10],
                              extrapolate: 'clamp',
                            })
                          }]
                        }
                      ]}
                    >
                      <Text style={[styles.timeText, isSmallScreen && styles.timeTextSmall, { color: this.props.themeValues.text }]}>{selectedHours}</Text>
                    </Animated.View>
                  </View>

                  <Text style={[styles.timeColon, isSmallScreen && styles.timeColonSmall, { alignSelf: "center", color: this.props.themeValues.text, marginLeft: selectedHours < 10 ? -12 : 0 }]}>:</Text>

                  <View style={[styles.timeColumn, isSmallScreen && styles.timeColumnSmall]}>
                    <Text style={[styles.timeLabel, isSmallScreen && styles.timeLabelSmall, { color: this.props.themeValues.muted }]}>Minutes</Text>
                    <Animated.View
                      {...this.createDraggableNumber(selectedMinutes, 'minutes', { min: 0, max: 59 }).panHandlers}
                      style={[
                        styles.draggableNumber,
                        isSmallScreen && styles.draggableNumberSmall,
                        {
                          transform: [{
                            translateY: this.animatedValue.interpolate({
                              inputRange: [-100, 100],
                              outputRange: [-10, 10],
                              extrapolate: 'clamp',
                            })
                          }]
                        }
                      ]}
                    >
                      <Text style={[styles.timeText, isSmallScreen && styles.timeTextSmall, { color: this.props.themeValues.text }]}>
                        {selectedMinutes < 10 ? "0" + selectedMinutes : selectedMinutes}
                      </Text>
                    </Animated.View>
                  </View>
                </View>
              )}
            </View>

            <View style={{ alignItems: "center", marginBottom: 20 }}>
              <Text
                style={[
                  styles.largeText,
                  styles.control,
                  { color: this.props.themeValues.text },
                ]}
              >
                <Text style={{ color: this.props.themeValues.text }}>BPM: </Text>
                <Text style={{ color: this.props.themeValues.text, fontWeight: "bold" }}>{this.state.beatsPerMinute}</Text>
              </Text>
              <View
                style={{
                  height: 40,
                  width: 200,
                  justifyContent: "center",
                  alignItems: "center",
                }}
                ref={(ref) => {
                  this.sliderContainerRef = ref;
                }}
                onLayout={this.handleSliderContainerLayout}
                {...this.sliderPanResponder.panHandlers}
              >
                <View
                  style={{
                    height: 20 * scaleFactor,
                    width: 180 * scaleFactor,
                    backgroundColor: this.props.themeValues.border,
                    borderRadius: 10,
                    position: "relative",
                  }}
                  ref={(ref) => {
                    this.sliderTrackRef = ref;
                  }}
                  onLayout={this.handleSliderTrackLayout}
                >
                  <View
                    pointerEvents="none"
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      height: "100%",
                      width: `${((this.state.beatsPerMinute - 10) / (this.state.widerBeatRange ? 110 : 65)) * 100}%`,
                      backgroundColor: this.props.themeValues.primary,
                      borderRadius: 10,
                    }}
                  />
                  <View
                    pointerEvents="none"
                    style={{
                      position: "absolute",
                      left: `${((this.state.beatsPerMinute - 10) / (this.state.widerBeatRange ? 110 : 65)) * 100}%`,
                      top: -5,
                      width: 20 * scaleFactor, // Back to original size
                      height: 40 * scaleFactor,
                      backgroundColor: "red",
                      borderRadius: 10,
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: 10, // Ensure it's on top
                    }}
                  >
                    <View
                      pointerEvents="none"
                      style={{
                        width: 10 * scaleFactor,
                        height: 30 * scaleFactor,
                        backgroundColor: "#fff",
                        borderRadius: 5,
                      }}
                    />
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* Commented out redundant time display - now using YouTube-style progress bar with elapsed/total time */}
          {/*
          <View style={[styles.centered]}>
            {this.state.isBeating || this.state.isPaused ? (
              <View>
                <Text
                  style={[
                    styles.largeText,
                    { color: this.props.themeValues.text },
                  ]}
                >
                  Time remaining:{" "}
                  {this.getRemainingTime(
                    (this.state.interval *
                      (this.state.toBeat - this.state.beatCount)) /
                      1000,
                  )}
                </Text>
                <Text style={{ color: this.props.themeValues.text }}>
                  Beats remaining: {this.state.toBeat - this.state.beatCount}
                </Text>
              </View>
            ) : null}
          </View>
          */}
        </View>

        <View style={{ marginTop: 20, paddingBottom: 10 }}>
          <Text
            style={{
              color: this.props.themeValues.muted,
              fontSize: 12,
              textAlign: "center",
            }}
          >
            &copy; {new Date().getFullYear()} MeditRate. All rights reserved.
          </Text>
        </View>
      </SafeAreaView>
    );
  }
}
